import {
    Button,
    Col,
    notification,
    Row,
    Switch,
    Typography
} from 'antd';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { IoMdCalendar, IoMdDocument } from 'react-icons/io';

import ModalFecha from '../../components/modalFecha';
import { getAccMov } from '../../services/accountingMovementService';
import SalesOfficeSelect from './../../components/common/SalesOfficeSelect';
import useSalesOffices from './../../hooks/useSalesOffices';
import SatReportTable from './SatReportTable';
import { clientFullName } from './../../utils/client';

const { Title } = Typography;

const DownloadReportButton = ({ data, rptDetalle }) => {
    const commonHeaders = [
        { label: '# Ticket ', key: 'ticketNumber' },
        { label: 'Importe', key: 'value' },
        { label: 'Fecha', key: 'date' },
        { label: 'Cliente', key: 'clientFullName' }
    ];

    const allHeaders = [
        ...commonHeaders,
        { label: 'Plan', key: 'plan' },
        { label: 'Texto', key: 'text' },
        { label: 'Oficina', key: 'oficina' }
    ];

    return <CSVLink
        data={data}
        headers={rptDetalle ? allHeaders : commonHeaders}
        filename="Reporte-Corte.csv"
        className="ant-btn ant-btn-primary">
        Descargar reporte
    </CSVLink>
};

export default function SatReport() {
    const [dataTable, setDataTable] = useState([]);
    const [dataTable2, setDataTable2] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [selectedFechas, setSelectedFechas] = useState([]);
    const [rptDetalle, setRptDetalle] = useState(false);
    const [selectedSalesOffice, setSelectedSalesOficce] = useState([]);
    const controllerRef = useRef(null);

	const { data: dataSalesOffice } = useSalesOffices();

	useEffect(() => {
		// Sales Offices own by current user
      	const ownedOffices = dataSalesOffice?.filter(d => d.isOwnedByCurrentUser) || [];
	
		if (ownedOffices.length === 1)
            setSelectedSalesOficce([ownedOffices[0].id]);

    }, [dataSalesOffice]);

    useEffect(() => {
        return () => {
            controllerRef.current && controllerRef.current.abort();
        }
    }, []);

    const handleOkModalFecha = data => {
        const items = [];
        for (let i = 0; i < data.length; i++) {
            const element = data[i];

            const f = moment(element.low, "DD-MM-YYYY").format('YYYY-MM-DDTHH:mm:ss');
            let f2 = null;

            if (element.high)
                f2 = moment(element.high, "DD-MM-YYYY").format('YYYY-MM-DDTHH:mm:ss');

            items.push({
                key: items.length + 1,
                option: element.option,
                sign: element.sign,
                low: f,
                high: f2,
            });

        }
        setSelectedFechas(items);
        setOpenModal(false);
    };

    const handleClickReport = async () => {
        if (selectedSalesOffice.length === 0)
            return notification.info({ message: 'Reporte', description: 'Selecciona oficina(s) de ventas.' });
        
        setDataTable([]);
        setDataTable2([]);
        
        const request = {
            clientPostings: true,
            dateRange: selectedFechas,
            salesOffices: selectedSalesOffice
        };
        
        controllerRef.current = new AbortController();
        setLoading(true);
            
        try {
            const { data } = await getAccMov(request, { signal: controllerRef?.current.signal });
            
            if (data.length === 0) {
                notification.info({ message: 'Reporte', description: 'No se encontraron datos con los filtros seleccionados.' });
                setLoading(false);
                return false;
            }
            
            const dataModel = [];
            const dataModel2 = [];
            let indx = 1;
            for (let x = 0; x < data.length; x++) {
                const header = data[x];
                if (header.accountingDetails.length > 0) {
                    for (let y = 0; y < data[x].accountingDetails.length; y++) {
                        const detail = data[x].accountingDetails[y];
                        if (header.ticketNumber !== null && detail.accountingTypeId === 'PC' && detail.debitCreditIndicator === 'D') { // TODO: Filtrar en backend

                            const item = {
                                key: indx,
                                ticketNumber: header.ticketNumber,
                                date: header.date,
                                client: header.client, // TODO Regresar RFC y razon social
                                clientFullName: clientFullName(header.client),
                                value: header.cancellationId !== null ? 0 : detail.value,
                            };

                            dataModel.push(item);
                            dataModel2.push({
                                ...item,
                                plan: detail.product.name,
                                planId: detail.product.id,
                                text: detail.text,
                                oficina: header.salesOffice.name
                            });
                            indx++;
                        }
                    }
                }
            }
            fillTableSimple(dataModel);
            fillTableDetalle(dataModel2);
        } catch (error) {
            // Ignore aborted requests
            if(error.code && error.code === 'ERR_CANCELED') return;
        } finally {
            setLoading(false);
        }
    };

    const fillTableDetalle = (data) => {
        const result = data.reduce(function (res, value) {
            const idx = res[0].indexOf(value.ticketNumber + '-' + value.planId);
            if (idx > -1) {
                res[1][idx].value += value.value
                if (value.text)
                    res[1][idx].text ? res[1][idx].text += ' / ' + value.text : res[1][idx].text = value.text;

            } else {
                res[0].push(value.ticketNumber + '-' + value.planId);
                res[1].push(value);
            }
            return res;
        }, [[], []]);
        const dataOrder = result[1].sort(function (a, b) {
            return a.ticketNumber - b.ticketNumber;
        });
        setDataTable(dataOrder);
    }

    const fillTableSimple = (data) => {
        const result2 = data.reduce(function (res, value) {
            const idx = res[0].indexOf(value.ticketNumber);
            if (idx > -1) {
                res[1][idx].value += value.value
            } else {
                res[0].push(value.ticketNumber);
                res[1].push(value);
            }
            return res;
        }, [[], []]);
        const dataOrder2 = result2[1].sort(function (a, b) {
            return a.ticketNumber - b.ticketNumber;
        });
        setDataTable2(dataOrder2);
    }

    const { dataSource, canExport } = useMemo(() => {
        return {
            dataSource: rptDetalle ? dataTable : dataTable2,
            canExport: (dataTable && dataTable.length > 0) || (dataTable2 && dataTable2.length > 0)
        };
    }, [dataTable, dataTable2, rptDetalle]);
 
    return (
        <>
            <ModalFecha
                modalVisibleFecha={openModal}
                handleCancelModalFecha={() => setOpenModal(false)}
                handleOkModalFecha={handleOkModalFecha}
            />
            <Row gutter={[24, 24]} style={{ marginTop: '2rem', marginLeft: '2rem', marginRight: '2rem' }}>
                <Col span={24}>
				    <Title level={2}>Reporte Corte</Title>
				    <hr className="hr-title" />
			    </Col>
                <Col span={24}>
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Row justify="start" gutter={16}>
                                <Col>
                                    <SalesOfficeSelect
							            mode="multiple"
							            style={{ width: 380 }}
							            onChange={setSelectedSalesOficce}
							            value={selectedSalesOffice}
							            placeholder="Oficina de ventas..."
							            maxTagCount="responsive"
							            onlyOwnedByCurrentUser
                                    />
                                </Col>
                                <Col>
                                    <Button
                                        type='primary'
                                        icon={<IoMdCalendar />}
                                        onClick={() => setOpenModal(true)}>
                                        &nbsp; Fechas...
                                    </Button>
                                </Col>
                                <Col style={{ paddingTop: 4 }}>
                                    <Switch checkedChildren="Corte" unCheckedChildren="SAT" onChange={(checked) => setRptDetalle(checked)} />
                                </Col>
                            </Row>
                        </Col>
                        <Col>
                            <Row justify="end" gutter={16}>
                                {canExport && <Col>
                                    <DownloadReportButton data={dataSource} rptDetalle={rptDetalle} />
                                </Col>}
                                <Col>
                                {loading
                                    ? <Button type="ghost" onClick={() => controllerRef?.current.abort()}>Cancelar</Button>
                                    : <Button type='primary'icon={<IoMdDocument />} onClick={handleClickReport}>&nbsp; Generar</Button>
                                }
                                </Col>
                            </Row>
                        </Col>
                    </Row>
			    </Col>
                <Col span={24}>
                    <SatReportTable dataSource={dataSource} loading={loading} rptDetalle={rptDetalle} />
                </Col>
            </Row>
        </>
    );
}
